import React, { useState } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Row,
  Col,
  Button,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
  Table,
} from "reactstrap";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import axiosInstance from "Axiosinstance";
import CompanyFormModal from "./Companyformmodal";
import CompaniesTable from "./Companiestable";
import BranchesTable from "./BranchTable";

/**
 * Displays a single company's details in a card layout.
 *
 * Props:
 * - company: object with company data
 * - readOnly: if true, hides the edit option entirely (default: false)
 * - onSaved: callback fired after a successful edit (e.g. refetch)
 * - updateEndpoint: API endpoint passed through to the edit modal
 */

// Maps raw purpose keys to a friendly label + icon
const PURPOSE_LABELS = {
  bag_protection_plan: {
    label: "Bag Protection Plan",
    icon: "mdi-bag-checked",
  },
  qr_bag_with_protection: {
    label: "QR Bag with Protection",
    icon: "mdi-qrcode",
  },
  etags: { label: "E-Tags", icon: "mdi-tag-outline" },
};

// Fallback: turns any unmapped snake_case key into Title Case
const formatPurposeLabel = (key) =>
  key
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  return `₹${Number(value).toLocaleString("en-IN")}`;
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const passwordSchema = Yup.object({
  newPassword: Yup.string()
    .matches(
      /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[@$!%*?&^#()_+\-=[\]{};':"\\|,.<>/?]).{8,}$/,
      "Password must be at least 8 characters and include uppercase, lowercase, number, and special character"
    )
    .required("New password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("newPassword")], "Passwords must match")
    .required("Confirm password is required"),
});

const PasswordChangeModal = ({ isOpen, toggle }) => (
  <Modal isOpen={isOpen} toggle={toggle} centered>
    <Formik
      initialValues={{ newPassword: "", confirmPassword: "" }}
      validationSchema={passwordSchema}
      onSubmit={async (values, { setSubmitting, resetForm }) => {
        try {
          const { data } = await axiosInstance.post(
            "/api/partner/companypasswordchange",
            values
          );
          if (!data?.status) throw new Error(data?.message);
          toast.success(data.message || "Verification link sent to your email");
          resetForm();
          toggle();
          window.location.assign("/logout");
        } catch (error) {
          toast.error(
            error?.response?.data?.message ||
              error?.message ||
              "Unable to request password change"
          );
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ errors, touched, isSubmitting }) => (
        <Form>
          <ModalHeader toggle={toggle}>Update Password</ModalHeader>
          <ModalBody>
            <p className="text-muted small">
              We will email a verification link before changing your password.
            </p>
            <div className="mb-3">
              <Label for="newPassword">New Password</Label>
              <Field
                id="newPassword"
                name="newPassword"
                type="password"
                className={`form-control ${errors.newPassword && touched.newPassword ? "is-invalid" : ""}`}
              />
              <ErrorMessage name="newPassword" component="div" className="invalid-feedback" />
            </div>
            <div>
              <Label for="confirmPassword">Confirm Password</Label>
              <Field
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                className={`form-control ${errors.confirmPassword && touched.confirmPassword ? "is-invalid" : ""}`}
              />
              <ErrorMessage name="confirmPassword" component="div" className="invalid-feedback" />
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" type="button" onClick={toggle}>Cancel</Button>
            <Button color="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Sending..." : "Send Verification Link"}
            </Button>
          </ModalFooter>
        </Form>
      )}
    </Formik>
  </Modal>
);

const CompanyCard = ({
  company,
  readOnly = false,
  onSaved,
  updateEndpoint,
}) => {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);

  if (!company) return null;

  const {
    company_name,
    company_key_representative_email,
    company_phone_country_code,
    company_phone,
    no_of_branches,
    company_address,
    company_gst_no,
    company_state,
    created_date,
    updated_date,
    purpose = [],
    branches = [],
    rate_of_insurance,
    wallet_balance,
    transaction_history = [],
  } = company;

  const history = Array.isArray(transaction_history)
    ? [...transaction_history].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      )
    : [];

  const formatDate = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (isNaN(d.getTime())) return "-";
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const Field = ({ label, value, md = 4 }) => (
    <Col md={md} className="mb-3">
      <div className="text-muted small">{label}</div>
      <div className="fw-medium">{value || "-"}</div>
    </Col>
  );

  return (
    <>
      <Card className="shadow-sm border-0 company-card">
        <CardHeader className="bg-white d-flex align-items-center justify-content-between border-bottom py-3">
          <div className="d-flex align-items-center">
            <div className="company-avatar me-3">
              <i className="mdi mdi-domain font-size-24 text-primary" />
            </div>
            <div>
              <h4 className="mb-0">{company_name || "Unnamed Company"}</h4>
              <small className="text-muted">Company Profile</small>
            </div>
          </div>

          {!readOnly && (
            <div className="d-flex gap-2">
              {/*
                    <Button color="secondary" size="sm" onClick={() => setIsPasswordModalOpen(true)}>
                <i className="mdi mdi-lock-reset me-1" />
                Update Password
              </Button>
               */}
        
              <Button color="primary" size="sm" onClick={() => setIsEditOpen(true)}>
                <i className="mdi mdi-pencil me-1" />
                Edit Company
              </Button>
            </div>
          )}
        </CardHeader>

        <CardBody>
          {/* Contact Info */}
          <section className="mb-4">
            <h6 className="text-uppercase text-muted fw-bold mb-3 section-heading">
              Contact Information
            </h6>
            <Row>
              <Field
                label="Key Representative Email"
                value={company_key_representative_email}
              />
              <Field
                label="Phone"
                value={
                  (company_phone_country_code || "+91") +
                  " " +
                  (company_phone || "-")
                }
              />
              <Col md={4} className="mb-3">
                <div className="text-muted small">Total Branches</div>
                <div className="fw-medium">
                  <Badge color="light" className="text-dark border">
                    {no_of_branches || 0}
                  </Badge>
                </div>
              </Col>
              {(wallet_balance !== undefined && wallet_balance !== null) && (
                <Field
                  label="Wallet Balance"
                  value={`₹${Number(wallet_balance).toLocaleString("en-IN")}`}
                />
              )}
              {readOnly && Object.entries(rate_of_insurance || {}).map(([coverage, rate]) => (
                <Field
                  key={coverage}
                  label={`Rate for ₹${Number(coverage).toLocaleString("en-IN")} Insurance`}
                  value={`₹${Number(rate).toLocaleString("en-IN")}`}
                />
              ))}
            </Row>
          </section>

          {/* Company Details */}
          <section className="mb-4">
            <h6 className="text-uppercase text-muted fw-bold mb-3 section-heading">
              Company Details
            </h6>
            <Row>
              <Field label="Address" value={company_address} md={6} />
              <Field label="State" value={company_state} md={3} />
              <Field label="GST Number" value={company_gst_no} md={3} />
            </Row>
          </section>

          {/* Access Options */}
          <section className="mb-4">
            <h6 className="text-uppercase text-muted fw-bold mb-3 section-heading">
              Enabled Services
            </h6>
            {purpose.length > 0 ? (
              <div className="d-flex flex-wrap gap-2">
                {purpose.map((key) => {
                  const meta = PURPOSE_LABELS[key];
                  return (
                    <span key={key} className="purpose-pill">
                      <i
                        className={`mdi ${
                          meta?.icon || "mdi-check-circle-outline"
                        } me-1`}
                      />
                      {meta?.label || formatPurposeLabel(key)}
                    </span>
                  );
                })}
              </div>
            ) : (
              <div className="text-muted">No access options assigned</div>
            )}
          </section>

          {/* Record Info */}
          <section>
            <h6 className="text-uppercase text-muted fw-bold mb-3 section-heading">
              Record Information
            </h6>
            <Row>
              <Field label="Created On" value={formatDate(created_date)} md={3} />
              <Field
                label="Last Updated"
                value={formatDate(updated_date)}
                md={3}
              />
            </Row>
          </section>

          <section>
            <h6 className="text-uppercase text-muted fw-bold mb-3 section-heading">
              Branch Details
            </h6>
            {branches.length > 0 ? (
              <BranchesTable
  branches={company.branches || []}
  companyUid={company.company_uid}
  onSaved={onSaved} // reuse the same refetch you pass into CompanyFormModal
  readOnly={readOnly}
/>
            ) : (
              <div className="text-muted">No branches found</div>
            )}
          </section>

          {!readOnly && (
          <section className="mt-4">
            <h6 className="text-uppercase text-muted fw-bold mb-3 section-heading">
              Transaction History
            </h6>
            {history.length === 0 ? (
              <div className="text-muted">No transactions yet</div>
            ) : (
              <div className="table-responsive">
                <Table className="table-nowrap align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Type</th>
                      <th>Amount</th>
                      <th>Balance After</th>
                      <th>Policy Number</th>
                      <th>Reason</th>
                      <th>Created By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((txn, index) => (
                      <tr key={`${txn.created_at}-${index}`}>
                        <td>{formatDateTime(txn.created_at)}</td>
                        <td>
                          <Badge
                            color={
                              txn.type === "credit" ? "success" : "danger"
                            }
                            className="text-uppercase"
                          >
                            {txn.type}
                          </Badge>
                        </td>
                        <td>{formatCurrency(txn.amount)}</td>
                        <td>{formatCurrency(txn.balance_after)}</td>
                        <td>{txn.policy_number || "—"}</td>
                        <td>{txn.reason || "—"}</td>
                        <td>{txn.created_by || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </section>
          )}
        </CardBody>
      </Card>

      {!readOnly && (
        <CompanyFormModal
          isOpen={isEditOpen}
          toggle={() => setIsEditOpen(false)}
          isEditing={true}
          editingCompany={company}
          onSaved={() => {
            setIsEditOpen(false);
            onSaved && onSaved();
          }}
          updateEndpoint={updateEndpoint}
          company_login = {true}
        />
      )}

      {!readOnly && (
        <PasswordChangeModal
          isOpen={isPasswordModalOpen}
          toggle={() => setIsPasswordModalOpen(false)}
        />
      )}

      <style>{`
        .company-avatar {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          background: rgba(64, 81, 137, 0.08);
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .section-heading {
          letter-spacing: 0.05em;
          font-size: 0.75rem;
        }
        .purpose-pill {
          display: inline-flex;
          align-items: center;
          padding: 6px 14px;
          border-radius: 20px;
          background: rgba(64, 81, 137, 0.08);
          color: #405189;
          font-size: 0.85rem;
          font-weight: 500;
        }
        .purpose-pill i {
          font-size: 1rem;
        }
      `}</style>
    </>
  );
};

export default CompanyCard;
